import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { updateVehicle } from "../../views/Fleet/fleetApi";
import { ErrorLine } from "./ForkliftDialogs";

/* ------------------------------------------------------------------
   Truck edits — rename and move branch. Trucks come from Bistrack,
   which can't be changed from here, so these are stored as overrides
   (forks.vehicle_overrides) that the website and the mobile app both
   apply. Remove / Restore use ConfirmDialog (ForkliftDialogs.js).
------------------------------------------------------------------ */

// Same limit as the server (fleetData.js VEHICLE_NAME_MAX).
const NAME_MAX = 40;

const collapse = (value) => String(value ?? "").trim().replace(/\s+/g, " ");

/**
 * Where a truck's own name and branch come from — Bistrack for most, the
 * last inspection for a truck that's left Bistrack, the tracker for a
 * GPS-only unit. `own` reads "Use <own> name (#134)".
 */
export const vehicleOrigin = (vehicle) => {
  if (vehicle?.inBistrack) return { hint: (value) => `Bistrack: ${value}`, own: "Bistrack's" };
  if (vehicle?.vehicleId) return { hint: (value) => `Recorded as ${value}`, own: "the recorded" };
  return { hint: (value) => `GPS Insight: ${value}`, own: "the tracker's" };
};

export const EditVehicleDialog = ({ vehicle, branches = [], open, onClose, onSaved, userId }) => {
  const [name, setName] = useState("");
  const [branch, setBranch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // "" = the truck's own (Bistrack) branch.
  const currentBranch = vehicle?.moved ? vehicle.branch : "";

  // Fill the form when the dialog opens — not every time the page's
  // two-minute refresh hands over a new vehicle object, which would wipe
  // what's being typed.
  const vehicleRef = useRef(vehicle);
  vehicleRef.current = vehicle;
  useEffect(() => {
    const current = vehicleRef.current;
    if (open && current) {
      setName(current.label);
      setBranch(current.moved ? current.branch : "");
      setError(null);
    }
  }, [open, vehicle?.key]);

  const options = useMemo(() => {
    const own = String(vehicle?.bistrackBranch || "").toLowerCase();
    const names = new Map();
    [...branches, currentBranch].forEach((b) => {
      if (b && b.toLowerCase() !== own && !names.has(b.toLowerCase())) names.set(b.toLowerCase(), b);
    });
    return [...names.values()].sort((a, b) => a.localeCompare(b));
  }, [branches, currentBranch, vehicle]);

  if (!vehicle) return null;
  const origin = vehicleOrigin(vehicle);
  const ownLabel = vehicle.bistrackLabel || vehicle.label;
  const nextName = collapse(name);
  const changes = {
    // A blank name goes back to Bistrack's.
    ...(nextName !== vehicle.label ? { name: nextName } : {}),
    ...(branch !== currentBranch ? { branch } : {}),
  };
  const nameKey = (value) => value.replace(/^#/, "").trim().toLowerCase();
  const isOwnName = !nextName || nameKey(nextName) === nameKey(ownLabel);

  const save = async () => {
    if (saving) return;
    if (!Object.keys(changes).length) return onClose();
    setSaving(true);
    setError(null);
    try {
      const result = await updateVehicle(userId, vehicle.key, changes);
      onSaved(result.vehicle);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 2 } }} aria-labelledby="edit-vehicle-title">
      <div className="p-5 sm:p-6">
        <h2 id="edit-vehicle-title" className="text-lg font-semibold">
          Edit truck {vehicle.label}
        </h2>
        <p className="app-muted mt-1 text-sm">
          Changes how the truck is listed here and in the mobile app. Bistrack isn't changed, and the truck keeps its
          inspection history and its GPS tracker.
        </p>
        <div className="mt-5 space-y-4">
          <div>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={name}
              autoFocus
              placeholder={ownLabel}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
              inputProps={{ maxLength: NAME_MAX }}
              helperText={origin.hint(ownLabel)}
            />
            {!isOwnName && (
              <button
                type="button"
                className="app-link mt-1 text-sm font-medium"
                onClick={() => setName(ownLabel)}
              >
                Use {origin.own} name ({ownLabel})
              </button>
            )}
          </div>
          <FormControl fullWidth size="small">
            <InputLabel id="edit-vehicle-branch" shrink>
              Branch
            </InputLabel>
            <Select
              labelId="edit-vehicle-branch"
              label="Branch"
              notched
              displayEmpty
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
            >
              <MenuItem value="">
                {vehicle.bistrackBranch ? `Use ${origin.own} branch (${vehicle.bistrackBranch})` : "No branch"}
              </MenuItem>
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <ErrorLine>{error}</ErrorLine>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
